import {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import "./CustomerManager.css";
import {compareService} from "../../service/dataComparator/dataCompareService";
import {authService} from "../../service/auth/auth.service";
import DuplicateResolutionModal from "./DuplicateResolutionModal";

const NOT_AVAILABLE = "N/A";
const NOT_APPLICABLE = "NOT APPLICABLE";

const orNotAvailable = (value) => (value !== null && value !== undefined && value !== "" ? value : NOT_AVAILABLE);

// The admin lookup returns snake_case keys (phone_no, loyalty_id); the compare endpoints return camelCase.
const SUMMARY_FIELDS = [
    ["Email", ["email"]],
    ["Phone", ["phone_no", "phoneNo"]],
    ["Loyalty ID", ["loyalty_id", "loyaltyId"]]
];

const formatComparisonSummary = (summary) => {
    // Set instead of per-field results when a source record is missing (e.g. "one or more customer not found").
    if (summary.summary) {
        return summary.summary;
    }
    return SUMMARY_FIELDS
        .map(([label, keys]) => `${label}: ${keys.map((key) => summary[key]).find((value) => value != null) ?? NOT_AVAILABLE}`)
        .join(" · ");
};

const normalizeCosmosLike = (record) => {
    if (!record) return null;
    return {
        firstName: orNotAvailable(record.firstName),
        lastName: orNotAvailable(record.lastName),
        email: orNotAvailable(record.email),
        phone: orNotAvailable(record.phoneNo),
        loyaltyId: orNotAvailable(record.loyaltyId),
        storeId: orNotAvailable(record.storeNumber != null ? String(record.storeNumber) : null),
        dob: orNotAvailable(record.birthday)
    };
};

const normalizeKlaviyoAdmin = (record) => {
    if (!record) return null;
    return {
        firstName: orNotAvailable(record.firstName),
        lastName: orNotAvailable(record.lastName),
        email: orNotAvailable(record.email),
        phone: orNotAvailable(record.phoneNumber),
        loyaltyId: orNotAvailable(record.loyaltyId),
        storeId: orNotAvailable(record.storeNumber != null ? String(record.storeNumber) : null),
        dob: orNotAvailable(record.dateOfBirth)
    };
};

const normalizeAuth0Admin = (record) => {
    if (!record) return null;
    return {
        firstName: orNotAvailable(record.first_name),
        lastName: orNotAvailable(record.last_name),
        email: orNotAvailable(record.email),
        phone: orNotAvailable(record.phone_number),
        loyaltyId: orNotAvailable(record.loyalty_ID),
        storeId: NOT_APPLICABLE,
        dob: NOT_APPLICABLE
    };
};

const FIELD_MAP = [
    ["First Name", "firstName"],
    ["Last Name", "lastName"],
    ["Email", "email"],
    ["Phone", "phone"],
    ["Loyalty ID", "loyaltyId"],
    ["Store ID", "storeId"],
    ["DOB", "dob"]
];

const REQUEST_FIELD_BY_KEY = {
    firstName: "firstName",
    lastName: "lastName",
    email: "email",
    phone: "phoneNo",
    loyaltyId: "loyaltyId",
    storeId: "storeNumber",
    dob: "birthday"
};

const OTHER_SOURCE_CONFIG = [
    {
        key: "klaviyo", name: "Klaviyo", creatable: true,
        normalize: normalizeKlaviyoAdmin,
        getFromLookup: (lookup) => lookup?.meta?.klaviyoProfiles?.[0] ?? null
    },
    {
        key: "inmar", name: "Inmar", creatable: true,
        normalize: normalizeCosmosLike,
        getFromLookup: (lookup) => lookup?.meta?.inmarCustomer ?? null
    },
    {
        key: "auth0", name: "Auth0", creatable: false,
        normalize: normalizeAuth0Admin,
        getFromLookup: (lookup) => lookup?.meta?.auth0Details ?? null
    },
    {
        key: "wynshop", name: "Wynshop", creatable: true,
        normalize: normalizeCosmosLike,
        getFromLookup: (lookup) => lookup?.meta?.wynshopCustomer ?? null
    }
];

const buildCosmosSources = (cosmosRecords) => {
    if (cosmosRecords.length === 0) {
        return [{
            key: "cosmos", name: "Cosmos", sourceType: "cosmos", creatable: true,
            record: null, exists: false, normalized: null, sourceCustomerId: null
        }];
    }
    return cosmosRecords.map((record, index) => ({
        key: `cosmos-${record.id ?? index}`,
        name: cosmosRecords.length > 1 ? `Cosmos #${index + 1}` : "Cosmos",
        sourceType: "cosmos",
        creatable: true,
        record,
        exists: true,
        normalized: normalizeCosmosLike(record),
        sourceCustomerId: record.id
    }));
};

const buildSourcesFromLookup = (lookupData) => {
    const cosmosRecords = lookupData?.data ?? [];
    const primaryCustomerId = cosmosRecords[0]?.id ?? null;

    const otherSources = OTHER_SOURCE_CONFIG.map(({key, name, creatable, normalize, getFromLookup}) => {
        const record = getFromLookup(lookupData) ?? null;
        return {
            key,
            name,
            sourceType: key,
            creatable,
            record,
            exists: record != null,
            normalized: normalize(record),
            sourceCustomerId: primaryCustomerId
        };
    });

    return [...buildCosmosSources(cosmosRecords), ...otherSources];
};

const buildInitialData = (sources) => {
    const data = {};
    FIELD_MAP.forEach(([label, fieldKey]) => {
        data[label] = sources.map((source) => (source.normalized ? source.normalized[fieldKey] : NOT_AVAILABLE));
    });
    return data;
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "");

const ACTIVITY_SORT_OPTIONS = [
    {value: "date-desc", label: "Newest First"},
    {value: "date-asc", label: "Oldest First"}
];

const ACTIVITY_QUICK_FILTERS = [
    {key: "create", label: "Create"},
    {key: "update", label: "Update"},
    {key: "delete", label: "Delete"},
    {key: "merge", label: "Merge"},
    {key: "inmar", label: "Inmar"},
    {key: "wynshop", label: "Wynshop"}
];

// Backend only accepts these three values for the activity log `source` filter.
const ACTIVITY_SOURCE_FILTERS = [
    {key: "auth0", label: "Auth0"},
    {key: "cosmos", label: "Cosmos"},
    {key: "klaviyo", label: "Klaviyo"}
];

const ACTIVITY_DATE_PRESETS = [
    {key: "1h", label: "Last Hour", hours: 1},
    {key: "3h", label: "Last 3 Hours", hours: 3},
    {key: "day", label: "Last Day", hours: 24},
    {key: "3day", label: "Last 3 Days", hours: 24 * 3},
    {key: "week", label: "Last Week", hours: 24 * 7},
    {key: "month", label: "Last Month", hours: 24 * 30},
    {key: "before", label: "Before"}
];

const computeActivityDateBounds = (preset, beforeDate) => {
    if (preset === "before") {
        const toTime = beforeDate ? new Date(beforeDate).setHours(23, 59, 59, 999) : null;
        return {fromTime: null, toTime};
    }

    const config = ACTIVITY_DATE_PRESETS.find((option) => option.key === preset);
    if (!config) return {fromTime: null, toTime: null};

    const toTime = Date.now();
    return {fromTime: toTime - config.hours * 60 * 60 * 1000, toTime};
};

const filterAndSortActivity = (activity, {searchText, quickFilters, fromTime, toTime, sortOrder}) => {
    const search = searchText.trim().toLowerCase();

    const filtered = activity.filter((item) => {
        const haystack = `${item.eventName ?? ""} ${item.message ?? ""} ${item.source ?? ""}`.toLowerCase();

        if (search && !haystack.includes(search)) return false;
        if (quickFilters.length > 0 && !quickFilters.every((key) => haystack.includes(key))) return false;

        const itemTime = item.time ? new Date(item.time).getTime() : null;
        if (fromTime !== null && (itemTime === null || itemTime < fromTime)) return false;
        return !(toTime !== null && (itemTime === null || itemTime > toTime));


    });

    return [...filtered].sort((a, b) => {
        const aTime = a.time ? new Date(a.time).getTime() : 0;
        const bTime = b.time ? new Date(b.time).getTime() : 0;
        return sortOrder === "date-asc" ? aTime - bTime : bTime - aTime;
    });
};

const detectFieldType = (value) => {
    if (value.includes("@") && value.includes(".")) return "email";

    if (/^\d{10}$/.test(value)) return "phoneNo";
    return "loyaltyId";
};

export default function CustomerDataTab({lookupData}) {

    const todayDateString = useMemo(() => new Date().toISOString().slice(0, 10), []);

    const [activeLookup, setActiveLookup] = useState(lookupData);

    const [activeTab, setActiveTab] = useState("compare");

    const [sources, setSources] = useState([]);

    const [customerData, setCustomerData] = useState({});

    // Last saved values per source; edits are compared against this to decide what changed.
    const [baselineData, setBaselineData] = useState({});

    const [changedFields, setChangedFields] = useState({});

    const [actionInFlight, setActionInFlight] = useState(false);

    const [activity, setActivity] = useState([]);

    const [activityLoading, setActivityLoading] = useState(false);

    const [activitySearch, setActivitySearch] = useState("");

    const [activityQuickFilters, setActivityQuickFilters] = useState([]);

    const [activitySource, setActivitySource] = useState("");

    const [activityDatePreset, setActivityDatePreset] = useState("");

    const [activityBeforeDate, setActivityBeforeDate] = useState("");

    const [activitySortOrder, setActivitySortOrder] = useState("date-desc");

    const [banner, setBanner] = useState({
        type: "",
        message: ""
    });

    const [searchValue, setSearchValue] = useState("");

    const [searching, setSearching] = useState(false);

    const [lastSearchParams, setLastSearchParams] = useState(null);

    const [showDuplicateModal, setShowDuplicateModal] = useState(false);

    const activeCustomerId = activeLookup?.data?.[0]?.id;

    const cosmosRecords = activeLookup?.data ?? [];

    const klaviyoProfiles = activeLookup?.meta?.klaviyoProfiles ?? [];

    const hasDeleteAccess = activeLookup?.meta?.hasDeleteAccess ?? false;

    const showMessage = (type, message) => {
        setBanner({type, message});
        setTimeout(() => {
            setBanner({
                type: "",
                message: ""
            });
        }, 3000);
    };

    useEffect(() => {
        setActiveLookup(lookupData);
    }, [lookupData]);

    useEffect(() => {
        const nextSources = buildSourcesFromLookup(activeLookup);
        setSources(nextSources);
        const initialData = buildInitialData(nextSources);
        setCustomerData(initialData);
        setBaselineData(initialData);
        setChangedFields({});

        const lookupRef = activeLookup?.meta?.lookupRef;
        if (lookupRef?.fieldType && lookupRef?.searchValue) {
            setLastSearchParams({fieldType: lookupRef.fieldType, searchValue: lookupRef.searchValue});
        }

        const nextCosmosRecords = activeLookup?.data ?? [];
        const nextKlaviyoProfiles = activeLookup?.meta?.klaviyoProfiles ?? [];
        setShowDuplicateModal(nextCosmosRecords.length > 1 || nextKlaviyoProfiles.length > 1);
    }, [activeLookup]);

    useEffect(() => {
        if (activeTab !== "activity" || !activeCustomerId) return;

        setActivityLoading(true);
        const params = {customerId: activeCustomerId};
        if (activitySource) params.source = activitySource;
        compareService.getActivityLogs(params).then((response) => {
            setActivity(Array.isArray(response) ? response : []);
            setActivityLoading(false);
        }, () => {
            showMessage("error", "Failed to load activity logs");
            setActivityLoading(false);
        });
    }, [activeTab, activeCustomerId, activitySource]);

    useEffect(() => {
        setActivitySearch("");
        setActivityQuickFilters([]);
        setActivitySource("");
        setActivityDatePreset("");
        setActivityBeforeDate("");
        setActivitySortOrder("date-desc");
    }, [activeCustomerId]);

    const {fromTime: activityFromTime, toTime: activityToTime} = useMemo(
        () => computeActivityDateBounds(activityDatePreset, activityBeforeDate),
        [activityDatePreset, activityBeforeDate]
    );

    const visibleActivity = useMemo(() => filterAndSortActivity(activity, {
        searchText: activitySearch,
        quickFilters: activityQuickFilters,
        fromTime: activityFromTime,
        toTime: activityToTime,
        sortOrder: activitySortOrder
    }), [activity, activitySearch, activityQuickFilters, activityFromTime, activityToTime, activitySortOrder]);

    const toggleActivityQuickFilter = (key) => {
        setActivityQuickFilters((prev) =>
            prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
        );
    };

    const toggleActivitySource = (key) => {
        setActivitySource((prev) => (prev === key ? "" : key));
    };

    const toggleActivityDatePreset = (key) => {
        setActivityDatePreset((prev) => (prev === key ? "" : key));
        setActivityBeforeDate("");
    };

    const handleClearActivityFilters = () => {
        setActivitySearch("");
        setActivityQuickFilters([]);
        setActivitySource("");
        setActivityDatePreset("");
        setActivityBeforeDate("");
        setActivitySortOrder("date-desc");
    };

    const updateValue = (field, sourceIndex, value) => {
        setCustomerData(prev => ({
            ...prev,
            [field]:
                prev[field].map((item, index) =>
                    index === sourceIndex ? value : item)
        }));

        // Only count a field as changed while it differs from the saved value, so reverting an edit clears it.
        const fieldKey = `${field}-${sourceIndex}`;
        setChangedFields(prev => {
            const next = {...prev};
            if (value === baselineData[field]?.[sourceIndex]) {
                delete next[fieldKey];
            } else {
                next[fieldKey] = true;
            }
            return next;
        });
    };

    const sourceHasChanges = (sourceIndex) =>
        Object.keys(changedFields).some((key) => key.endsWith(`-${sourceIndex}`));

    const sourceHasData = (sourceIndex) =>
        FIELD_MAP.some(([label]) => {
            const value = baselineData[label]?.[sourceIndex];
            return value !== undefined && value !== null && value !== "" && value !== NOT_AVAILABLE;
        });

    const hasAnyChanges = Object.keys(changedFields).length > 0;

    const markSourceSaved = (sourceIndex) => {
        setBaselineData(prev => {
            const next = {...prev};
            Object.keys(customerData).forEach((label) => {
                next[label] = (prev[label] ?? []).map((item, index) =>
                    index === sourceIndex ? customerData[label][index] : item);
            });
            return next;
        });
        setChangedFields(prev => Object.fromEntries(
            Object.entries(prev).filter(([key]) => !key.endsWith(`-${sourceIndex}`))
        ));
    };

    const buildSourceRequest = (sourceIndex, sourceType) => {
        const targetCustomerId = sources[sourceIndex]?.sourceCustomerId ?? activeCustomerId;
        const request = {customerId: targetCustomerId, source: sourceType};
        FIELD_MAP.forEach(([label, fieldKey]) => {
            const value = customerData[label]?.[sourceIndex];
            if (value === undefined || value === NOT_AVAILABLE) return;

            const requestKey = REQUEST_FIELD_BY_KEY[fieldKey];
            if (requestKey === "storeNumber") {
                const storeNumber = Number(value);
                if (!Number.isNaN(storeNumber)) request.storeNumber = storeNumber;
            } else {
                request[requestKey] = value;
            }
        });
        return request;
    };

    const handleAction = (sourceIndex, source, action) => {
        if (action === "delete" && !window.confirm(`Delete the ${source.name} record for this customer?`)) {
            return;
        }

        setActionInFlight(true);

        const targetCustomerId = source.sourceCustomerId ?? activeCustomerId;

        let promise;
        if (action === "create") {
            promise = compareService.createBySource(buildSourceRequest(sourceIndex, source.sourceType));
        } else if (action === "update") {
            promise = compareService.updateBySource(targetCustomerId, buildSourceRequest(sourceIndex, source.sourceType));
        } else {
            promise = compareService.deleteBySource(targetCustomerId, source.sourceType);
        }

        promise.then((response) => {
            setActionInFlight(false);
            if (response?.error) {
                showMessage("error", `${source.name} ${action} failed: ${response.error}`);
                return;
            }
            if (action === "update") {
                markSourceSaved(sourceIndex);
            }
            showMessage("success", `${source.name} ${action} successful`);
        }, () => {
            setActionInFlight(false);
            showMessage("error", `${source.name} ${action} failed`);
        });
    };

    const handleSaveAll = () => {
        const cosmosIndex = sources.findIndex((source) => source.sourceCustomerId === activeCustomerId && source.key.startsWith("cosmos"));
        if (cosmosIndex === -1) return;

        setActionInFlight(true);
        compareService.updateAll(buildSourceRequest(cosmosIndex, "cosmos")).then((response) => {
            setActionInFlight(false);
            if (response?.error) {
                showMessage("error", response.error);
                return;
            }
            showMessage("success", "Changes saved successfully");
        }, () => {
            setActionInFlight(false);
            showMessage("error", "Failed to save changes");
        });
    };

    const handleCancel = () => {
        setCustomerData(baselineData);
        setChangedFields({});
    };

    const runLookup = (fieldType, value, {showSummary = true} = {}) => {
        return authService.lookUpAccount({fieldType, searchValue: value}).then((response) => {
            if (!response || response.error) {
                showMessage("error", response?.error || "Lookup failed");
                return;
            }

            if (!response.data?.[0]?.id) {
                showMessage("error", "No customer found for that search");
                return;
            }

            setActiveTab("compare");
            setActiveLookup(response);

            const summary = response.meta?.comparisonSummary;
            if (summary && showSummary) {
                showMessage(summary.compareDataSummary ? "success" : "error", formatComparisonSummary(summary));
            }
        }, () => {
            showMessage("error", "Lookup failed");
        });
    };

    const handleSearch = () => {
        const trimmed = searchValue.trim();
        if (!trimmed) return;

        setSearching(true);
        runLookup(detectFieldType(trimmed), trimmed).then(() => setSearching(false));
    };

    const refreshLookup = (successMessage) => {
        // Show the action result first; the refresh only replaces it if the lookup itself fails.
        if (successMessage) {
            showMessage("success", successMessage);
        }
        if (!lastSearchParams) return;
        runLookup(lastSearchParams.fieldType, lastSearchParams.searchValue, {showSummary: !successMessage});
    };

    const renderSourceActions = (source, index) => {
        if (source.exists) {
            return (
                <>
                    <button
                        type="button"
                        className="update"
                        disabled={actionInFlight || !sourceHasChanges(index)}
                        title={sourceHasChanges(index) ? "" : "No changes to update"}
                        onClick={() =>
                            handleAction(index, source, "update")
                        }
                    >
                        Update
                    </button>

                    {hasDeleteAccess && (
                        <button
                            type="button"
                            className="delete"
                            disabled={actionInFlight || !sourceHasData(index)}
                            title={sourceHasData(index) ? "" : "No data to delete"}
                            onClick={() =>
                                handleAction(index, source, "delete")
                            }
                        >
                            Delete
                        </button>
                    )}
                </>
            );
        }

        if (source.creatable) {
            return (
                <button
                    type="button"
                    className="create"
                    disabled
                    onClick={() =>
                        handleAction(index, source, "create")
                    }
                >
                    Create
                </button>
            );
        }

        return (
            <div className="not-creatable">
                Cannot create {source.name} profile
            </div>
        );
    };

    return (
        <div className="customer-container">
            {
                showDuplicateModal &&
                <DuplicateResolutionModal
                    cosmosRecords={cosmosRecords}
                    klaviyoProfiles={klaviyoProfiles}
                    customerId={activeCustomerId}
                    hasDeleteAccess={hasDeleteAccess}
                    onResolved={refreshLookup}
                    onDismiss={() => setShowDuplicateModal(false)}
                />
            }

            {
                banner.message &&
                <div className={`banner ${banner.type}`}>
                    {banner.type === "success" ? "✓" : "✖"}
                    {" "}
                    {banner.message}
                </div>
            }

            <div className="header">
                <div className="tabs">
                    <button
                        type="button"
                        className={activeTab === "compare" ? "tab active" : "tab"}
                        onClick={() => setActiveTab("compare")}
                    >
                        Compare Sheet
                    </button>

                    <button
                        type="button"
                        className={activeTab === "activity" ? "tab active" : "tab"}
                        onClick={() => setActiveTab("activity")}
                    >
                        Activity Log
                    </button>
                </div>


                {
                    activeTab === "activity" ?
                        <div className="search-group">
                            <input
                                id="activity-search"
                                className="search"
                                aria-label="Search activity"
                                placeholder="Search by event, message or source"
                                value={activitySearch}
                                onChange={(e) => setActivitySearch(e.target.value)}
                            />
                            <button type={"button"} className="clear-filters-btn" onClick={handleClearActivityFilters}>
                                Clear Filters
                            </button>
                        </div> :
                        <div className="search-group">
                            <input
                                className="search"
                                placeholder="Verify Email / Loyalty ID / Phone"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            />
                            <button type={"button"} className="save"
                                    disabled={searching}
                                    onClick={handleSearch}>
                                {searching ? "Verifying..." : "Verify"}
                            </button>
                        </div>
                }
            </div>


            {
                !activeCustomerId &&
                <p>No customer selected.</p>
            }

            {
                activeCustomerId && activeTab === "compare" &&
                <div className="table-container">
                    <table>
                        <thead>
                        <tr>
                            <th>
                                Field
                            </th>
                            {
                                sources.map((source, index) => (
                                    <th key={source.key}>
                                        <div className="source-title">
                                            {source.name}
                                        </div>

                                        <div className={source.exists ? "connected" : "not-found"}>
                                            {source.exists ? "🟢 Record Exists" : "🔴 No Record"}
                                        </div>
                                        <div>
                                            {renderSourceActions(source, index)}
                                        </div>
                                    </th>
                                ))
                            }
                        </tr>
                        </thead>
                        <tbody>
                        {
                            Object.entries(customerData)
                                .map(([field, values]) => (
                                    <tr key={field}>
                                        <td className="field">
                                            {field}
                                        </td>
                                        {
                                            values.map((value, index) => {
                                                const changed = changedFields[`${field}-${index}`];
                                                let inputClassName = "";

                                                if (value === NOT_AVAILABLE) {
                                                    inputClassName = "missing";
                                                } else if (changed) {
                                                    inputClassName = "changed";
                                                }

                                                return (
                                                    <td key={`${field}-${index}`}>
                                                        <input
                                                            className={inputClassName}
                                                            value={value}
                                                            onChange={(e) =>
                                                                updateValue(
                                                                    field,
                                                                    index,
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                );
                                            })
                                        }

                                    </tr>
                                ))
                        }
                        </tbody>
                    </table>
                </div>
            }

            {
                activeTab === "activity" &&
                <div className="activity">

                    <div className="activity-toolbar">
                        <div className="activity-filters">
                            <fieldset className="activity-filter-group">
                                <legend className="form-label">
                                    Source
                                </legend>

                                <div className="activity-quick-filters">
                                    {ACTIVITY_SOURCE_FILTERS.map((option) => (
                                        <button
                                            key={option.key}
                                            type="button"
                                            className={
                                                activitySource === option.key
                                                    ? "quick-filter active"
                                                    : "quick-filter"
                                            }
                                            onClick={() => toggleActivitySource(option.key)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </fieldset>

                            <div className="activity-filter-group">
                                <div className="form-label">
                                    Quick Filters
                                </div>
                                <div className="activity-quick-filters">
                                    {
                                        ACTIVITY_QUICK_FILTERS.map((option) => (
                                            <button
                                                key={option.key}
                                                type="button"
                                                className={
                                                    activityQuickFilters.includes(option.key)
                                                        ? "quick-filter active"
                                                        : "quick-filter"
                                                }
                                                onClick={() => toggleActivityQuickFilter(option.key)}
                                            >
                                                {option.label}
                                            </button>
                                        ))
                                    }
                                </div>
                            </div>
                            <div className="activity-filter-group">
                                <label htmlFor="dateRange">
                                    Date Range
                                </label>
                                <div className="activity-date-presets">
                                    <div className="activity-quick-filters">
                                        {
                                            ACTIVITY_DATE_PRESETS.map((option) => (
                                                <button
                                                    key={option.key}
                                                    type="button"
                                                    className={
                                                        activityDatePreset === option.key
                                                            ? "quick-filter active"
                                                            : "quick-filter"
                                                    }
                                                    onClick={() => toggleActivityDatePreset(option.key)}
                                                >
                                                    {option.label}
                                                </button>
                                            ))
                                        }
                                    </div>
                                    {
                                        activityDatePreset === "before" &&
                                        <input
                                            className="activity-date-range-input"
                                            type="date"
                                            aria-label="Before date"
                                            value={activityBeforeDate}
                                            max={todayDateString}
                                            onChange={(e) => setActivityBeforeDate(e.target.value)}
                                        />
                                    }
                                </div>
                            </div>

                            <div className="activity-filter-group">
                                <label htmlFor="activity-sort">
                                    Sort By
                                </label>
                                <div className="activity-quick-filters">
                                    {
                                        ACTIVITY_SORT_OPTIONS.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={
                                                    activitySortOrder === option.value
                                                        ? "quick-filter active"
                                                        : "quick-filter"
                                                }
                                                onClick={() => setActivitySortOrder(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    {
                        activityLoading &&
                        <p>Loading activity...</p>
                    }

                    {
                        !activityLoading && activity.length === 0 &&
                        <p>No activity found.</p>
                    }

                    {
                        !activityLoading && activity.length > 0 && visibleActivity.length === 0 &&
                        <p>No activity matches the selected filters.</p>
                    }

                    {
                        !activityLoading && visibleActivity.length > 0 &&
                        <div className="activity-log-list">
                            {
                                visibleActivity.map((item, index) => (
                                    <div className="log" key={`${item.time}-${item.eventName}-${index}`}>
                                        <div>
                                            {formatDateTime(item.time)}
                                        </div>
                                        <div>
                                            {item.eventName}
                                        </div>
                                        <div>
                                            {item.message}
                                        </div>
                                        <div>
                                            {item.source}
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    }
                </div>
            }

            {
                activeTab === "compare" && activeCustomerId &&
                <div className="footer">
                    <button type={"button"} className="delete"
                            disabled={actionInFlight || !hasAnyChanges}
                            onClick={handleCancel}>
                        Cancel
                    </button>

                    <button type={"button"} className="save"
                            disabled={actionInFlight || !hasAnyChanges}
                            onClick={handleSaveAll}>
                        Save All Sources
                    </button>
                </div>
            }
        </div>
    );
}

CustomerDataTab.propTypes = {
    lookupData: PropTypes.shape({
        data: PropTypes.array,
        meta: PropTypes.object
    })
};
